import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import { Grid } from '@progress/kendo-react-grid';

const openedCollapsible = (maxHeight, theme) => ({
  maxHeight,
  transition: theme.transitions.create('max-height', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});
const closedCollapsible = (maxHeight, theme) => ({
  maxHeight,
  transition: theme.transitions.create('max-height', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
});

export const GridStyled = styled(Grid, {
  shouldForwardProp: (prop) =>
    prop !== 'hasDetail' &&
    prop !== 'isFooterDetails' &&
    prop !== 'height' &&
    prop !== 'maxHeight' &&
    prop !== 'open' &&
    prop !== 'gridWidth' &&
    prop !== 'noBorders' &&
    prop !== 'hasTransparentBackground' &&
    prop !== 'noMarginTop' &&
    prop !== 'remainingGridHeight' &&
    prop !== 'gridColor' &&
    prop !== 'scrollColor' &&
    prop !== 'firefoxSecScrollColor' &&
    prop !== 'hasHorizontalScroll' &&
    prop !== 'hasVerticalScroll',
})(({
  theme,
  hasDetail,
  isFooterDetails,
  height,
  maxHeight,
  open,
  gridWidth,
  noBorders,
  hasTransparentBackground,
  noMarginTop,
  remainingGridHeight,
  gridColor,
  scrollColor,
  firefoxSecScrollColor,
  hasHorizontalScroll,
  hasVerticalScroll,
}) => {
  const getBackgroundColor = (section) => {
    if (section === 'footer td') {
      if (hasTransparentBackground)
        return theme.palette.colors.secondaryBackground;

      return theme.palette.colors.dividerOnPrimaryBackground;
    }

    if (section === 'footer') {
      if (hasTransparentBackground) return 'transparent';

      return theme.palette.colors.primaryBackground;
    }

    if (section === 'remainingGridHeight')
      return gridColor || theme.palette.colors.secondaryBackground;

    if (hasTransparentBackground) return 'transparent';

    if (isFooterDetails)
      return theme.palette.colors.selectedAndDetailBackground;

    return theme.palette.colors.primaryBackground;
  };

  const getMainContainerMargin = () => {
    if (noMarginTop) return 0;

    if (isFooterDetails) return '8px';

    return '24px';
  };

  const getHeaderBorderRadius = () => {
    const radius = '5px';

    if (noMarginTop)
      return {
        borderBottomLeftRadius: radius,
        borderBottomRightRadius: radius,
      };

    return { borderRadius: radius };
  };

  return {
    width: gridWidth ?? '100%',
    border: 'none !important',
    overflowY: 'auto',
    marginTop: getMainContainerMargin(),
    backgroundColor: getBackgroundColor(),
    '&.k-grid': {
      height: height && `${height} !important`,
      maxHeight,
      ...(open && {
        ...openedCollapsible(maxHeight, theme),
      }),
      ...(open === false && {
        ...closedCollapsible(maxHeight, theme),
      }),

      '& .k-master-row .k-grid-content-sticky': {
        borderColor: theme.palette.colors.borderLine,
      },
    },
    '& th': {
      border: 'none !important',
    },
    '& thead': {
      '& tr': {},
    },
    '& tfoot': {
      '& tr': {},
    },
    '& .k-grid-content': {
      overflowY: 'auto',
      '& .k-grid-table': {
        width: '100% !important',
      },
      '&::-webkit-scrollbar': {
        height: '6px',
        width: '6px',
      },
      '&::-moz-scrollbar': {
        height: '6px',
        width: '6px',
      },
      backgroundColor: getBackgroundColor(),
      '&::-webkit-scrollbar-thumb': {
        backgroundColor:
          scrollColor || theme.palette.colors.scrollOnPrimaryBackground,
        borderRadius: '5px',
      },
      '&::-webkit-scrollbar-corner': {
        backgroundColor: hasTransparentBackground
          ? 'transparent'
          : theme.palette.colors.primaryBackground,
      },
      '&::-moz-scrollbar-thumb': {
        backgroundColor: theme.palette.colors.scrollOnPrimaryBackground,
        borderRadius: '5px',
      },

      '@-moz-document url-prefix()': {
        scrollbarColor: `${
          scrollColor || theme.palette.colors.scrollOnPrimaryBackground
        } ${firefoxSecScrollColor || theme.palette.colors.primaryBackground}`,
        scrollbarWidth: 'thin',
      },

      '& .trl-grid-filler-row': {
        height: `${remainingGridHeight ?? 0}px`,

        '&:hover': {
          backgroundColor: `${getBackgroundColor('remainingGridHeight')}`,
        },
      },
      '& .trl-grid-filler-cell': {
        backgroundColor: `${getBackgroundColor('remainingGridHeight')}`,
        borderTop: 'none !important',
        borderRadius: '5px',
        height: `${remainingGridHeight ?? 0}px`,
        padding: 0,
      },

      paddingRight: hasVerticalScroll && !hasHorizontalScroll ? '4px' : 0,
      '& .k-table': {
        paddingRight: hasVerticalScroll && hasHorizontalScroll ? '4px' : 0,
      },
    },

    '& .k-grid-footer td': {
      border: 'none',
      borderTop: `solid 1px ${theme.palette.colors.borderLine}`,
      borderBottom: `solid 1px ${theme.palette.colors.borderLine} !important`,
      backgroundColor: getBackgroundColor('footer td'),
      marginBottom: '4px',
      color: theme.palette.colors.onPrimaryBackground,
      height: '30px',
      padding: '1px !important',
      '&:first-of-type': {
        borderTopLeftRadius: '5px',
        borderBottomLeftRadius: '5px',
        borderLeft: `solid 1px ${theme.palette.colors.borderLine}`,
      },
    },

    '& .k-table-row.k-selected > td, .k-table-row.k-selected > .k-table-td': {
      backgroundColor: isFooterDetails
        ? 'unset'
        : `${theme.palette.colors.selectedAndDetailBackground} !important`,
      '@-moz-document url-prefix()': {
        borderRadius: 0,
      },
    },

    '& .k-grid-header-wrap, .k-grid-footer-wrap': {
      border: 'none',
    },

    '& .k-grid-header-wrap': {
      display: 'flex',

      '& .k-grid-header-table': {
        width: '100% !important',
      },
    },

    '& .k-grid-header .k-table-th.k-grid-header-sticky, .k-grid-footer': {
      color: theme.palette.colors.mainFocused,
      fontWeight: '500 !important',
    },
    '& .k-table-th.k-grid-header-sticky': {
      backgroundColor: gridColor || theme.palette.colors.secondaryBackground,
    },
    '& tr:not(.trl-grid-filler-row)': {
      '& td': {
        border: 'none',
        borderTop: !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,
        borderBottom:
          !noBorders &&
          `solid 1px ${theme.palette.colors.borderLine} !important`,
        whiteSpace: 'nowrap',
        '&:first-of-type': hasDetail
          ? {
              borderTopLeftRadius: '5px',
              borderBottomLeftRadius: '5px',

              '&::before': {
                content: `''`,
                position: 'absolute',
                backgroundColor: theme.palette.colors.mainFocused,
                width: '5px',
                height: '110%',
                bottom: 0,
                left: 0,
                borderBottomLeftRadius: '5px',
              },
            }
          : {
              borderTopLeftRadius: '5px',
              borderBottomLeftRadius: '5px',

              borderLeft:
                !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,

              '&::before': {
                backgroundColor: 'transparent !important',
              },
            },
        '&:last-of-type.k-grid-content-sticky': {
          borderRight:
            !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,
        },
        '&:last-child': {
          borderTopRightRadius: '5px',
          borderBottomRightRadius: '5px',
          borderRight:
            !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,

          '&.k-grid-content-sticky::before': {
            backgroundColor: 'transparent !important',
          },
        },
        '&.k-table-td': {
          borderTop: 'none',
        },
      },

      '&.k-master-row.k-table-row.k-selected td.k-grid-content-sticky::before':
        {
          backgroundColor: theme.palette.colors.mainFocused,
        },
    },
    '& .k-grid-content-sticky': {
      borderTop: !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,
      backgroundColor: `${
        gridColor || theme.palette.colors.secondaryBackground
      } !important`,
      boxShadow: `0px -2px 0 ${getBackgroundColor()}, 0px 2px 0px 0px ${getBackgroundColor()}`,
    },
    '& .k-master-row:hover .k-grid-content-sticky': {
      backgroundColor:
        !isFooterDetails &&
        `${theme.palette.colors.selectedAndDetailBackground} !important`,
    },

    '& .k-master-row:has(+ tr.k-detail-row:hover) .k-grid-content-sticky': {
      backgroundColor: `${theme.palette.colors.selectedAndDetailBackground} !important`,
    },

    '& .k-grid-md td, .k-grid-md .k-table-td': {
      padding: '0 12px',
    },

    '& table': {
      borderCollapse: 'separate',
      borderSpacing: '0 4px !important',
    },
    '& .k-grid-header': {
      border: !noBorders && `solid 1px ${theme.palette.colors.borderLine}`,
      backgroundColor: gridColor || theme.palette.colors.secondaryBackground,
      marginBottom: !isFooterDetails && '4px',
      color: theme.palette.colors.mainFocused,
      height: '48px',
      padding: '1px',
      ...getHeaderBorderRadius(),
    },
    '& .k-grid-footer': {
      padding: '1px',
      border: 'none',
      backgroundColor: getBackgroundColor('footer'),
    },
    '& .k-table-thead': {
      backgroundColor: gridColor || theme.palette.colors.secondaryBackground,
      textTransform: 'lowercase',
    },
    '& .k-table-tfoot': {
      backgroundColor: theme.palette.colors.dividerOnPrimaryBackground,
    },
    '& .k-table-th': {
      verticalAlign: 'bottom',
      color: theme.palette.colors.mainFocused,
      fontWeight: '500 !important',
      fontSize: '12px',
      padding: '0 12px !important',
    },
    '& .k-grid-footer-table .k-table-row .k-table-td': {
      color: theme.palette.colors.onPrimaryBackground,
      fontWeight: '500 !important',
      fontFamily: 'Roboto Mono',
      fontSize: '12px',
      padding: '0 12px !important',
    },
    '& .k-table-row': {
      color: theme.palette.colors.onSecondaryAndDetailBackground,
      fontWeight: '500 !important',
      fontSize: '12px',
    },
    '& .k-grid-container tr': {
      backgroundColor: `${
        gridColor || theme.palette.colors.secondaryBackground
      } !important`,
      height: isFooterDetails ? '32px' : '40px',
      zIndex: 999,
      '&:hover, :hover + tr.k-detail-row': {
        backgroundColor:
          !isFooterDetails &&
          `${theme.palette.colors.selectedAndDetailBackground} !important`,
      },
    },
    '& .k-grid-container tr.k-selected + tr.k-detail-row': {
      backgroundColor: `${theme.palette.colors.selectedAndDetailBackground} !important`,
    },
    '& .k-grid-container tr:has(+ tr.k-detail-row:hover)': {
      backgroundColor: `${theme.palette.colors.selectedAndDetailBackground} !important`,
    },
    '& .k-detail-row': {
      position: 'relative',
      top: '-14px',
      '& td:last-child': {
        borderTopRightRadius: 0,
      },
    },
    '& .multiHeader': {
      '& span': {
        justifyContent: 'center',
        display: 'grid',
      },
    },
    '& th.k-header.customClassRight': {
      '& span': {
        justifyContent: 'flex-end',
      },
    },
    '& th.k-header.customClassCenter': {
      '& span': {
        justifyContent: 'center',
      },
    },
    '& tr .customClassRight': {
      textAlign: 'right',
    },
    '& tr .customClassCenter': {
      textAlign: 'center',
      '& div': {
        display: 'flex',
        margin: 'auto',
      },
    },
    '& tr .customClassNumber': {
      textAlign: 'right',
      fontFamily: 'Roboto Mono',
      fontSize: '12px',
      fontWeight: 500,
      paddingRight: '25px',
    },
    '& .k-grid-norecords-template': {
      backgroundColor: `${getBackgroundColor('remainingGridHeight')}`,
      border: 'none',
      color: theme.palette.colors.onSecondaryAndDetailBackground,
      height: '100%',
      padding: 0,
      width: '100%',
      borderRadius: '5px',
      borderBottom: hasHorizontalScroll
        ? `4px solid ${theme.palette.colors.primaryBackground} !important` // infoBackground
        : 'none',
    },
    '#simpleOrdersBookContainer &, #topInstrumentsContainer &, #latestNewsContainer &':
      {
        '& .k-grid-norecords-template': {
          borderBottom: hasHorizontalScroll
            ? `4px solid ${theme.palette.colors.infoBackground} !important`
            : 'none',
        },
      },
  };
});

export const ArrowStyled = styled(FontAwesomeIcon)(() => ({
  height: '10px',
  width: '9px',
}));

export const MultiHeaderColStyled = styled('th')(({ theme }) => ({
  '&.k-table-th.k-header.multiHeader': {
    borderBottom: `solid 1px ${theme.palette.colors.borderLine} !important`,
    textAlign: 'center',
  },
}));

export const TdExpandedStyled = styled('td')(({ theme }) => ({
  paddingLeft: '19px !important',
  textOverflow: 'unset !important',
  cursor: 'pointer',
  color: theme.palette.colors.mainFocused,
}));

export const NormalTableCell = styled('td', {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' &&
    prop !== 'dataIndex' &&
    prop !== 'rowType' &&
    prop !== 'columnsCount' &&
    prop !== 'columnIndex' &&
    prop !== 'selectionChange' &&
    prop !== 'dataItem' &&
    prop !== 'ariaColumnIndex' &&
    prop !== 'render',
})(() => ({}));

export const NormalTableRow = styled('tr', {
  shouldForwardProp: (prop) =>
    prop !== 'isSelected' &&
    prop !== 'dataIndex' &&
    prop !== 'absoluteRowIndex' &&
    prop !== 'rowHeight' &&
    prop !== 'selectedField' &&
    prop !== 'isHidden' &&
    prop !== 'rowType' &&
    prop !== 'isInEdit' &&
    prop !== 'isAltRow' &&
    prop !== 'dataItem' &&
    prop !== 'ariaRowIndex' &&
    prop !== 'render',
})(() => ({}));
